import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chart, ChartDataset, registerables } from "chart.js";
import { COUNTRY_AREAS } from "../home/constants";
import { useLanguage } from "../../i18n/LanguageProvider";
import {
  ModelComparisonResponse,
  ModelErrorSums,
  useAnalyticsApi,
} from "./analyticsService";

Chart.register(...registerables);

// Admin: Standard (unified LightGBM) vs Pro (Timestral) vs a seasonal-naive
// baseline, scored against actual spot prices in every zone we forecast. The
// server returns error sums per model/zone/lead day and per model/zone/day; all
// pooling (country, all zones, lead buckets) happens here.

type ModelKey = "standard" | "pro";
const MODELS: ModelKey[] = ["standard", "pro"];
const MODEL_COLOR: Record<ModelKey, string> = { standard: "#2054a2", pro: "#7c3aed" };
const NAIVE_COLOR = "#9ca3af";

const ALL_ZONES = Object.values(COUNTRY_AREAS).flat();
const COUNTRIES = Object.keys(COUNTRY_AREAS);

const LEAD_BUCKETS: { key: string; from: number; to: number }[] = [
  { key: "0-1", from: 0, to: 0 },
  { key: "1-2", from: 1, to: 1 },
  { key: "2-7", from: 2, to: 6 },
  { key: "7-14", from: 7, to: 13 },
  { key: "14-30", from: 14, to: 99 },
];

interface Pooled {
  n: number;
  mae: number | null; // öre/kWh
  rmse: number | null;
  bias: number | null;
  naiveMae: number | null;
  skill: number | null; // % better than naive
}

// SEK/kWh sums -> öre/kWh means.
const pool = (rows: ModelErrorSums[]): Pooled => {
  let n = 0, abs = 0, sq = 0, err = 0, nn = 0, nabs = 0;
  for (const r of rows) {
    n += r.n; abs += r.sumAbs; sq += r.sumSq; err += r.sumErr;
    nn += r.naiveN; nabs += r.naiveSumAbs;
  }
  const mae = n ? (100 * abs) / n : null;
  const naiveMae = nn ? (100 * nabs) / nn : null;
  return {
    n,
    mae,
    rmse: n ? 100 * Math.sqrt(sq / n) : null,
    bias: n ? (100 * err) / n : null,
    naiveMae,
    skill: mae != null && naiveMae ? 100 * (1 - mae / naiveMae) : null,
  };
};

const fmt = (v: number | null | undefined, digits = 1, suffix = "") =>
  v == null || !isFinite(v) ? "–" : `${v.toFixed(digits)}${suffix}`;
const fmtSigned = (v: number | null | undefined, digits = 1) =>
  v == null || !isFinite(v) ? "–" : `${v > 0 ? "+" : ""}${v.toFixed(digits)}`;

const LineChart: React.FC<{
  labels: string[];
  datasets: ChartDataset<"line", (number | null)[]>[];
  yLabel: string;
  height?: number;
}> = ({ labels, datasets, yLabel, height = 280 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    const tick = "#8b919a";
    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: { labels, datasets: datasets.map((d) => ({ spanGaps: true, ...d })) },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "bottom", labels: { boxWidth: 12, color: tick } },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${c.parsed.y == null ? "–" : c.parsed.y.toFixed(1)}`,
            },
          },
        },
        scales: {
          x: { ticks: { color: tick, maxRotation: 0, autoSkipPadding: 12 }, grid: { display: false } },
          y: {
            beginAtZero: true,
            title: { display: true, text: yLabel, color: tick },
            ticks: { color: tick },
            grid: { color: "rgba(128,128,128,0.15)" },
          },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [labels, datasets, yLabel]);

  return (
    <div style={{ height }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export const ModelComparison: React.FC = () => {
  const { t, locale } = useLanguage();
  const api = useAnalyticsApi();

  const [scope, setScope] = useState<string>("SE");
  const [days, setDays] = useState(30);
  const [anchorHour, setAnchorHour] = useState(10);
  const [overlap, setOverlap] = useState(true);
  const [data, setData] = useState<ModelComparisonResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await api.fetchModelComparison(ALL_ZONES, days, anchorHour, overlap));
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, anchorHour, overlap]);

  useEffect(() => { load(); }, [load]);

  const scopeZones = useMemo(
    () => (scope === "ALL" ? ALL_ZONES : COUNTRY_AREAS[scope] ?? [scope]),
    [scope],
  );
  const inScope = useCallback((a: string) => scopeZones.includes(a), [scopeZones]);

  const view = useMemo(() => {
    if (!data) return null;
    const lead = data.byLead.filter((r) => inScope(r.area));
    const byDay = data.byDay.filter((r) => inScope(r.area));
    const cheap = data.cheap.filter((r) => inScope(r.area));

    const totals = Object.fromEntries(
      MODELS.map((m) => [m, pool(lead.filter((r) => r.model === m))]),
    ) as Record<ModelKey, Pooled>;

    const cheapRate = Object.fromEntries(
      MODELS.map((m) => {
        const rows = cheap.filter((r) => r.model === m);
        const d = rows.reduce((s, r) => s + r.days, 0);
        const h = rows.reduce((s, r) => s + r.hits, 0);
        return [m, d ? { rate: (100 * h) / (3 * d), days: d } : null];
      }),
    ) as Record<ModelKey, { rate: number; days: number } | null>;

    const buckets = LEAD_BUCKETS.map((b) => ({
      key: b.key,
      ...Object.fromEntries(
        MODELS.map((m) => [
          m,
          pool(lead.filter((r) => r.model === m && r.leadDay >= b.from && r.leadDay <= b.to)),
        ]),
      ),
    })) as ({ key: string } & Record<ModelKey, Pooled>)[];

    // MAE per lead day. The naive baseline is the same series for both models
    // (identical hours when overlap is on), so take it from whichever has rows.
    const maxLead = lead.reduce((m, r) => Math.max(m, r.leadDay), -1);
    const leadDays = Array.from({ length: maxLead + 1 }, (_, i) => i);
    const leadSeries = (m: ModelKey) =>
      leadDays.map((d) => pool(lead.filter((r) => r.model === m && r.leadDay === d)));
    const leadStd = leadSeries("standard");
    const leadPro = leadSeries("pro");

    // Per forecast day, hours under 48 h ahead.
    const dayKeys = Array.from(new Set(byDay.map((r) => r.day))).sort();
    const shortMae = (m: ModelKey | "naive") =>
      dayKeys.map((d) => {
        const rows = byDay.filter((r) => r.day === d && (m === "naive" || r.model === m));
        if (m === "naive") {
          const src = rows.filter((r) => r.model === "standard").length
            ? rows.filter((r) => r.model === "standard")
            : rows;
          const n = src.reduce((s, r) => s + r.shortNaiveN, 0);
          return n ? (100 * src.reduce((s, r) => s + r.shortNaiveSumAbs, 0)) / n : null;
        }
        const n = rows.reduce((s, r) => s + r.shortN, 0);
        return n ? (100 * rows.reduce((s, r) => s + r.shortSumAbs, 0)) / n : null;
      });

    const zones = scopeZones.map((z) => ({
      zone: z,
      standard: pool(data.byLead.filter((r) => r.area === z && r.model === "standard")),
      pro: pool(data.byLead.filter((r) => r.area === z && r.model === "pro")),
    }));

    return {
      totals,
      cheapRate,
      buckets,
      lead: {
        labels: leadDays.map((d) => `${d + 1}`),
        std: leadStd.map((p) => p.mae),
        pro: leadPro.map((p) => p.mae),
        naive: leadDays.map((_, i) => (leadStd[i].n ? leadStd[i].naiveMae : leadPro[i].naiveMae)),
      },
      trend: {
        labels: dayKeys.map((d) =>
          new Date(`${d}T12:00:00Z`).toLocaleDateString(locale, { day: "numeric", month: "short" }),
        ),
        std: shortMae("standard"),
        pro: shortMae("pro"),
        naive: shortMae("naive"),
      },
      zones,
    };
  }, [data, inScope, scopeZones, locale]);

  const modelName = (m: ModelKey) => t(m === "pro" ? "models.pro" : "models.standard");

  const leadDatasets = useMemo<ChartDataset<"line", (number | null)[]>[]>(
    () =>
      view
        ? [
            { label: modelName("standard"), data: view.lead.std, borderColor: MODEL_COLOR.standard, backgroundColor: MODEL_COLOR.standard, tension: 0.25, pointRadius: 2 },
            { label: modelName("pro"), data: view.lead.pro, borderColor: MODEL_COLOR.pro, backgroundColor: MODEL_COLOR.pro, tension: 0.25, pointRadius: 2 },
            { label: t("models.naive"), data: view.lead.naive, borderColor: NAIVE_COLOR, backgroundColor: NAIVE_COLOR, borderDash: [5, 4], tension: 0.25, pointRadius: 0 },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, t],
  );
  const trendDatasets = useMemo<ChartDataset<"line", (number | null)[]>[]>(
    () =>
      view
        ? [
            { label: modelName("standard"), data: view.trend.std, borderColor: MODEL_COLOR.standard, backgroundColor: MODEL_COLOR.standard, tension: 0.25, pointRadius: 2 },
            { label: modelName("pro"), data: view.trend.pro, borderColor: MODEL_COLOR.pro, backgroundColor: MODEL_COLOR.pro, tension: 0.25, pointRadius: 2 },
            { label: t("models.naive"), data: view.trend.naive, borderColor: NAIVE_COLOR, backgroundColor: NAIVE_COLOR, borderDash: [5, 4], tension: 0.25, pointRadius: 0 },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, t],
  );

  // Lower is better for MAE; mark the better of the two per row.
  const better = (a: number | null, b: number | null): "a" | "b" | null =>
    a == null || b == null ? null : a < b ? "a" : b < a ? "b" : null;

  const unit = t("models.unit");
  const totals = view?.totals;
  const winner = totals ? better(totals.standard.mae, totals.pro.mae) : null;

  return (
    <div className="model-cmp">
      <div className="sp-filterbar">
        <span className="sp-filterbar__label">{t("models.title")}</span>
        <div className="model-cmp__controls">
          <select className="sp-select" value={scope} onChange={(e) => setScope(e.target.value)} aria-label={t("models.scope")}>
            <option value="ALL">{t("models.allZones")}</option>
            {COUNTRIES.map((c) => (
              <optgroup key={c} label={t(`country.${c}`)}>
                {COUNTRY_AREAS[c].length > 1 && (
                  <option value={c}>{t("models.wholeCountry", { country: t(`country.${c}`) })}</option>
                )}
                {COUNTRY_AREAS[c].map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <select className="sp-select" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t("models.period")}>
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>{t("analytics.lastDays", { count: d })}</option>
            ))}
          </select>
          <select className="sp-select" value={anchorHour} onChange={(e) => setAnchorHour(Number(e.target.value))} aria-label={t("models.anchor")}>
            {[4, 10, 16, 22].map((h) => (
              <option key={h} value={h}>{t("models.anchorAt", { hour: String(h).padStart(2, "0") })}</option>
            ))}
          </select>
          <label className="model-cmp__check">
            <input type="checkbox" checked={overlap} onChange={(e) => setOverlap(e.target.checked)} />
            {t("models.overlap")}
          </label>
        </div>
      </div>
      <p className="model-cmp__hint">{t("models.hint")}</p>

      {loading && !view && <div className="sp-empty"><span className="sp-spinner" /></div>}
      {error && <div className="sp-empty">{t("models.error")}</div>}

      {view && totals && (
        <div className={loading ? "model-cmp__body is-loading" : "model-cmp__body"}>
          {/* Head-to-head tiles */}
          <div className="model-cmp__tiles">
            {MODELS.map((m) => {
              const p = totals[m];
              const cheap = view.cheapRate[m];
              const isWinner = (winner === "a" && m === "standard") || (winner === "b" && m === "pro");
              return (
                <div key={m} className={`sp-tile model-cmp__tile ${isWinner ? "is-winner" : ""}`} style={{ borderTopColor: MODEL_COLOR[m] }}>
                  <div className="model-cmp__tile-head">
                    <span className="model-cmp__dot" style={{ background: MODEL_COLOR[m] }} />
                    <span className="model-cmp__tile-name">{modelName(m)}</span>
                    {isWinner && <span className="sp-chip sp-chip--success">{t("models.best")}</span>}
                  </div>
                  {p.n === 0 ? (
                    <div className="sp-tile__sub">{t("models.noRows")}</div>
                  ) : (
                    <div className="model-cmp__stats">
                      <div><span>{t("models.mae")}</span><strong>{fmt(p.mae)}</strong><em>{unit}</em></div>
                      <div><span>{t("models.skill")}</span><strong>{fmt(p.skill, 0, " %")}</strong><em>{t("models.vsNaive")}</em></div>
                      <div><span>{t("models.bias")}</span><strong>{fmtSigned(p.bias)}</strong><em>{unit}</em></div>
                      <div><span>{t("models.rmse")}</span><strong>{fmt(p.rmse)}</strong><em>{unit}</em></div>
                      <div><span>{t("models.cheap3")}</span><strong>{cheap ? fmt(cheap.rate, 0, " %") : "–"}</strong><em>{cheap ? t("models.days", { count: cheap.days }) : ""}</em></div>
                      <div><span>{t("models.hours")}</span><strong>{p.n.toLocaleString(locale)}</strong><em>{t("models.scored")}</em></div>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="sp-tile model-cmp__tile model-cmp__tile--naive" style={{ borderTopColor: NAIVE_COLOR }}>
              <div className="model-cmp__tile-head">
                <span className="model-cmp__dot" style={{ background: NAIVE_COLOR }} />
                <span className="model-cmp__tile-name">{t("models.naive")}</span>
              </div>
              <div className="model-cmp__stats">
                <div><span>{t("models.mae")}</span><strong>{fmt(totals.standard.naiveMae ?? totals.pro.naiveMae)}</strong><em>{unit}</em></div>
              </div>
              <div className="sp-tile__sub">{t("models.naiveDesc")}</div>
            </div>
          </div>

          {/* By lead time */}
          <div className="sp-card">
            <div className="sp-card__head">
              <h3 className="sp-card__title">{t("models.byLead")}</h3>
              <span className="sp-card__sub">{t("models.byLeadSub")}</span>
            </div>
            <div className="model-cmp__split">
              <LineChart labels={view.lead.labels} datasets={leadDatasets} yLabel={`${t("models.mae")} (${unit})`} />
              <table className="sp-table model-cmp__buckets">
                <thead>
                  <tr>
                    <th>{t("models.lead")}</th>
                    <th className="num">{modelName("standard")}</th>
                    <th className="num">{modelName("pro")}</th>
                    <th className="num">{t("models.naive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.buckets.map((b) => {
                    const w = better(b.standard.mae, b.pro.mae);
                    return (
                      <tr key={b.key}>
                        <td>{t("models.leadDays", { range: b.key })}</td>
                        <td className={`num ${w === "a" ? "is-best" : ""}`}>{fmt(b.standard.mae)}</td>
                        <td className={`num ${w === "b" ? "is-best" : ""}`}>{fmt(b.pro.mae)}</td>
                        <td className="num model-cmp__muted">{fmt(b.standard.naiveMae ?? b.pro.naiveMae)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Over time */}
          <div className="sp-card">
            <div className="sp-card__head">
              <h3 className="sp-card__title">{t("models.trend")}</h3>
              <span className="sp-card__sub">{t("models.trendSub")}</span>
            </div>
            <LineChart labels={view.trend.labels} datasets={trendDatasets} yLabel={`${t("models.mae")} (${unit})`} height={240} />
          </div>

          {/* Per zone */}
          <div className="sp-card">
            <div className="sp-card__head">
              <h3 className="sp-card__title">{t("models.perZone")}</h3>
              <span className="sp-card__sub">{unit}</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>{t("models.zone")}</th>
                    <th className="num">{t("models.mae")} {modelName("standard")}</th>
                    <th className="num">{t("models.mae")} {modelName("pro")}</th>
                    <th className="num">{t("models.naive")}</th>
                    <th className="num">{t("models.skill")} {modelName("standard")}</th>
                    <th className="num">{t("models.skill")} {modelName("pro")}</th>
                    <th className="num">{t("models.bias")} {modelName("standard")}</th>
                    <th className="num">{t("models.bias")} {modelName("pro")}</th>
                    <th className="num">{t("models.hours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.zones.map(({ zone, standard, pro }) => {
                    const w = better(standard.mae, pro.mae);
                    return (
                      <tr key={zone}>
                        <td><strong>{zone}</strong></td>
                        <td className={`num ${w === "a" ? "is-best" : ""}`}>{fmt(standard.mae)}</td>
                        <td className={`num ${w === "b" ? "is-best" : ""}`}>{fmt(pro.mae)}</td>
                        <td className="num model-cmp__muted">{fmt(standard.naiveMae ?? pro.naiveMae)}</td>
                        <td className="num">{fmt(standard.skill, 0, " %")}</td>
                        <td className="num">{fmt(pro.skill, 0, " %")}</td>
                        <td className="num">{fmtSigned(standard.bias)}</td>
                        <td className="num">{fmtSigned(pro.bias)}</td>
                        <td className="num model-cmp__muted">{Math.max(standard.n, pro.n).toLocaleString(locale)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
